import React, { useEffect, useMemo, useRef, useState } from "react";
import { Flex, Input, Popover, Spin, Typography } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { debounce } from "@/utilities/debounce";

const { Text } = Typography;
const { Search } = Input;

/** El verde de selección que ya usa el selector de operadores del dashboard. */
export const PEOPLE_FILTER_GREEN = "#15744b";
export const PEOPLE_FILTER_GREEN_BG = "#f0faf4";

/**
 * Lo común de los dos filtros: un disparador con aspecto de `Select`, un popover con buscador y
 * lista scrolleable, y la carga con debounce. **Sin paso de confirmación**: cierra con click
 * afuera y la selección ya se ve en los chips; nada se aplica a la tabla hasta el "Filtrar" del
 * side panel (spec de Contactos, "no exige confirmación intermedia").
 */
export interface PeopleFilterShellProps<Row> {
  placeholder: string;
  searchPlaceholder: string;
  emptyText: string;
  noResultsText: (q: string) => string;
  selectedCount: number;
  selectedSummary: string;
  disabled?: boolean;
  /** Vuelve a pedir cuando cambia: el `storeCodes` del dropdown general, por ejemplo. */
  fetchKey: string;
  fetchRows: (search: string, signal: AbortSignal) => Promise<Row[]>;
  renderRows: (rows: Row[]) => React.ReactNode;
  /** Chips de lo seleccionado, arriba del disparador. */
  chips?: React.ReactNode;
  width?: number;
  /**
   * Contactos/Presupuestos/Pedidos: los chips van adentro del propio buscador, como un
   * tag-input, y se puede seguir escribiendo al lado. El dashboard mantiene los chips en una
   * fila aparte arriba del disparador (`false`, default histórico de este componente).
   */
  chipsInline?: boolean;
}

export function PeopleFilterShell<Row>({
  placeholder,
  searchPlaceholder,
  emptyText,
  noResultsText,
  selectedCount,
  selectedSummary,
  disabled,
  fetchKey,
  fetchRows,
  renderRows,
  chips,
  width = 320,
  chipsInline = false,
}: PeopleFilterShellProps<Row>) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState<Row[] | null>(null);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  // El panel de resultados tiene que quedar del mismo ancho que el buscador, no el `width`
  // fijo por default — se mide el disparador real en vez de adivinar un número.
  const [panelWidth, setPanelWidth] = useState(width);

  const load = useMemo(
    () =>
      debounce(async (q: string) => {
        abortRef.current?.abort();
        const ctrl = new AbortController();
        abortRef.current = ctrl;
        setLoading(true);
        try {
          const data = await fetchRows(q, ctrl.signal);
          if (!ctrl.signal.aborted) setRows(data);
        } catch (e: any) {
          if (e?.name !== "CanceledError" && e?.code !== "ERR_CANCELED") {
            console.error("Error al cargar el filtro de personas:", e);
            if (!ctrl.signal.aborted) setRows([]);
          }
        } finally {
          if (!ctrl.signal.aborted) setLoading(false);
        }
      }, 400),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchKey],
  );

  // Cambió el recorte (los stores de arriba): lo cargado ya no vale.
  useEffect(() => {
    // Un request en vuelo del recorte anterior no puede poblar la lista nueva.
    abortRef.current?.abort();
    setRows(null);
    if (open) load(search);
    return () => load.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchKey]);

  const onOpenChange = (next: boolean) => {
    if (disabled) return;
    if (next && containerRef.current) {
      setPanelWidth(containerRef.current.offsetWidth);
    }
    setOpen(next);
    if (next && rows === null) load(search);
  };

  const onSearch = (q: string) => {
    setSearch(q);
    load(q);
  };

  const panel = (
    <div
      style={{
        width: panelWidth,
        display: "flex",
        flexDirection: "column",
        maxHeight: 420,
      }}
    >
      <div style={{ overflowY: "auto", flex: 1 }}>
        {loading && rows === null ? (
          <Flex justify="center" align="center" style={{ padding: 24 }}>
            <Spin size="small" />
          </Flex>
        ) : rows && rows.length === 0 ? (
          <div style={{ padding: 16, textAlign: "center" }}>
            <Text type="secondary">
              {search ? noResultsText(search) : emptyText}
            </Text>
          </div>
        ) : (
          <div
            style={{ opacity: loading ? 0.6 : 1, transition: "opacity 0.1s" }}
          >
            {renderRows(rows ?? [])}
          </div>
        )}
      </div>
    </div>
  );

  const trigger = chipsInline ? (
    <div
      role="button"
      aria-disabled={disabled}
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 4,
        padding: "4px 8px",
        minHeight: 32,
        border: `1px solid ${open ? PEOPLE_FILTER_GREEN : "#d9d9d9"}`,
        borderRadius: 6,
        background: disabled ? "#f5f5f5" : "#fff",
        cursor: disabled ? "not-allowed" : "text",
      }}
    >
      {chips}
      {open ? (
        <Input
          variant="borderless"
          autoFocus
          value={search}
          placeholder={selectedCount ? undefined : searchPlaceholder}
          onChange={(e) => onSearch(e.target.value)}
          style={{ flex: 1, minWidth: 80, padding: 0 }}
        />
      ) : (
        !selectedCount && (
          <Text type="secondary" style={{ flex: 1 }}>
            {placeholder}
          </Text>
        )
      )}
    </div>
  ) : open ? (
    <Search
      allowClear
      autoFocus
      value={search}
      placeholder={searchPlaceholder}
      onChange={(e) => onSearch(e.target.value)}
    />
  ) : (
    <div
      role="button"
      aria-disabled={disabled}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 8,
        padding: "4px 11px",
        minHeight: 32,
        border: "1px solid #d9d9d9",
        borderRadius: 6,
        background: disabled ? "#f5f5f5" : "#fff",
        cursor: disabled ? "not-allowed" : "pointer",
        color: disabled ? "rgba(0,0,0,0.25)" : undefined,
      }}
    >
      <Text
        type={selectedCount ? undefined : "secondary"}
        ellipsis
        style={{ flex: 1, color: disabled ? "inherit" : undefined }}
      >
        {selectedCount ? selectedSummary : placeholder}
      </Text>
      <DownOutlined style={{ fontSize: 12, color: "rgba(0,0,0,0.25)" }} />
    </div>
  );

  return (
    <Flex vertical gap={6}>
      {!chipsInline && chips && (
        <Flex wrap gap={6}>
          {chips}
        </Flex>
      )}
      <Popover
        open={open}
        onOpenChange={onOpenChange}
        trigger="click"
        placement="bottomLeft"
        arrow={false}
        overlayInnerStyle={{ padding: 0 }}
        content={panel}
        destroyTooltipOnHide={false}
      >
        <div ref={containerRef}>{trigger}</div>
      </Popover>
    </Flex>
  );
}
